/**
 * AppInstall.tsx - "Get the app" controls for the public share viewer.
 *
 * Small screens get a dismissible bottom bar (plus a sidebar entry that outlives
 * the dismissal); large screens get a header button. What a click does depends on
 * the device, not the width — see utilities/appInstall.ts. Nothing here navigates
 * or opens on its own: every store jump and dialog follows an explicit click.
 */

import { lazy, Suspense, useState } from 'react'
import { Button, CloseButton, Drawer, HStack, IconButton, Portal, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { LuSmartphone, LuX } from 'react-icons/lu'
import { STORE_BADGES, currentPlatform, installAction } from '@/utilities/appInstall'

// The QR code (and its encoder) is only fetched once a desktop visitor asks for it.
const AppQrDialog = lazy(() => import('./AppQrDialog'))

export const StoreBadgeRow: React.FC = () => (
    // The gap is the quarter-of-height clear space Google's badge requires.
    <HStack gap="10px" justify="center" wrap="wrap" dir="ltr">
        {STORE_BADGES.map(badge => (
            <a key={badge.href} href={badge.href} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', padding: 2 }}>
                <img src={badge.src} alt={badge.alt} width={badge.width} height={badge.height} style={{ height: 44, width: 'auto' }} />
            </a>
        ))}
    </HStack>
)

// Returns the click handler plus the dialog/sheet it may open, rendered by the caller.
export const useAppInstall = (isMobile: boolean) => {
    const { t, i18n } = useTranslation()
    const [qrOpen, setQrOpen] = useState(false)
    const [qrRequested, setQrRequested] = useState(false)
    const [sheetOpen, setSheetOpen] = useState(false)

    const openInstall = () => {
        const action = installAction(currentPlatform())
        if (action.kind === 'navigate') {
            window.location.href = action.url
        } else if (action.kind === 'choose' && isMobile) {
            setSheetOpen(true)
        } else {
            // Desktop, or an unrecognised device on a large screen: QR + both badges.
            setQrRequested(true)
            setQrOpen(true)
        }
    }

    const overlays = (
        <>
            {qrRequested && (
                <Suspense fallback={null}>
                    <AppQrDialog open={qrOpen} onClose={() => setQrOpen(false)} />
                </Suspense>
            )}
            <Drawer.Root open={sheetOpen} onOpenChange={(e) => setSheetOpen(e.open)} placement="bottom">
                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content dir={i18n.dir()} borderTopRadius="var(--cair-radius-lg)" paddingBottom="env(safe-area-inset-bottom)">
                            <Drawer.Header>
                                <Drawer.Title>{t('app_install_title') || 'Get the Mushtarek app'}</Drawer.Title>
                            </Drawer.Header>
                            <Drawer.Body paddingBottom="6">
                                <Text fontSize="sm" color="fg.muted" marginBottom="4">{t('app_install_choose_store') || 'Choose your app store.'}</Text>
                                <StoreBadgeRow />
                            </Drawer.Body>
                            <Drawer.CloseTrigger asChild>
                                <CloseButton size="md" aria-label={t('close') || 'Close'} />
                            </Drawer.CloseTrigger>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </>
    )

    return { openInstall, overlays }
}

export const InstallHeaderButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const { t } = useTranslation()
    return (
        <Button size="sm" flexShrink={0} background="var(--cair-accent)" color="white" borderRadius="var(--cair-radius-lg)" _hover={{ background: 'var(--cair-accent-hover)' }} onClick={onClick}>
            <LuSmartphone />{t('get_the_app') || 'Get the app'}
        </Button>
    )
}

// Sits in the main column's flex flow (not position:fixed), so it takes its own
// space below the conversation and never covers it.
const INSTALL_BAR_HEIGHT = 64

export const InstallBar: React.FC<{ onInstall: () => void; onDismiss: () => void }> = ({ onInstall, onDismiss }) => {
    const { t } = useTranslation()
    return (
        <HStack
            as="aside"
            aria-label={t('get_the_app') || 'Get the app'}
            flexShrink={0}
            minHeight={`calc(${INSTALL_BAR_HEIGHT}px + env(safe-area-inset-bottom))`}
            paddingTop="2"
            paddingBottom="max(8px, env(safe-area-inset-bottom))"
            paddingStart="4"
            paddingEnd="1"
            gap="2"
            background="var(--cair-surface-elevated)"
            borderTop="1px solid var(--cair-border)"
            boxShadow="0 -1px 3px rgba(0,0,0,0.06)"
        >
            <Text flex="1" minWidth={0} fontSize="sm" fontWeight="medium" textAlign="start" lineClamp={2}>
                {t('app_install_bar_text') || 'Try the Mushtarek app'}
            </Text>
            <Button size="sm" minHeight="44px" paddingX="3" flexShrink={0} background="var(--cair-accent)" color="white" borderRadius="var(--cair-radius-lg)" _hover={{ background: 'var(--cair-accent-hover)' }} onClick={onInstall}>
                {t('get_the_app') || 'Get the app'}
            </Button>
            <IconButton aria-label={t('dismiss') || 'Dismiss'} variant="ghost" color="fg.muted" minWidth="44px" height="44px" flexShrink={0} onClick={onDismiss}>
                <LuX />
            </IconButton>
        </HStack>
    )
}

export const SidebarInstallEntry: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const { t } = useTranslation()
    return (
        <Button variant="ghost" width="100%" minHeight="44px" justifyContent="flex-start" gap="2" color="var(--cair-accent)" _hover={{ background: 'var(--cair-accent-subtle)' }} onClick={onClick}>
            <LuSmartphone />{t('download_the_app') || 'Download the app'}
        </Button>
    )
}
